import { computed, ref } from 'vue';

import { axiosApi } from 'src/utils/axios';

const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
};

export type PushSubscriptionState = 'unknown' | 'unsupported' | 'needs-explainer' | 'subscribed' | 'missing' | 'deferred';

// Enrolment fails for two very different reasons and used to collapse into
// one: a refusal (nothing to retry) and a network failure at boot (everything
// to retry). The second silently cost a coach push for their whole shift.
const RESUBSCRIBE_BASE_DELAY_MS = 10_000;
const RESUBSCRIBE_MAX_DELAY_MS = 300_000;
let resubscribeTimer: ReturnType<typeof setTimeout> | null = null;
let resubscribeAttempt = 0;

/**
 * Channels item 2: push is a safety net, never a requirement — but "silently
 * losing the safety net is acceptable for nobody", so any user the feature
 * targets sees a visible in-app warning whenever their device has no active
 * subscription (denied or missing). Module-scope refs: one shared reactive
 * status for every `usePushSubscription()` caller (MainLayout's boot flow,
 * the pre-permission explainer, the missing-subscription warning).
 */
const state = ref<PushSubscriptionState>('unknown');

/**
 * The warning is dismissible, but only for the current app boot: this ref is
 * module-scope and deliberately never persisted, so relaunching the PWA (or
 * reloading the tab) surfaces it again. Dismissing therefore silences the
 * nag, never the fact — the app settings drawer carries a permanent
 * notification status row with the same enable action.
 */
const warningDismissed = ref(false);

/**
 * `Notification.permission === 'denied'`: retrying is a dead end (browsers
 * won't re-prompt), so the settings row points at the browser settings
 * instead of offering a button that can't work.
 */
const permissionBlocked = ref(false);

/**
 * `state` is evaluated for every signed-in user so the settings row can tell
 * anyone the truth about their device, but the explainer must not ambush a
 * user the alert channels don't target (admins get escalation email, never
 * push). It therefore opens only after an explicit opt-in gesture: MainLayout's
 * boot flow for the targeted audience, or the settings row's enable button for
 * everyone else.
 */
const explainerAllowed = ref(false);

/**
 * Enrols this device for web push (coach alerts + employee transit prompts).
 * Silent no-op on the actual browser permission when unsupported or denied
 * — push is an attention channel, never a requirement — but always updates
 * `state` so the in-app warning can surface honestly.
 *
 * iPhone caveat: push only works once the PWA is added to the home screen.
 */
export const usePushSubscription = () => {
  const evaluateSupport = (): boolean =>
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

  const clearResubscribeTimer = () => {
    if (resubscribeTimer !== null) {
      clearTimeout(resubscribeTimer);
      resubscribeTimer = null;
    }
  };

  const scheduleResubscribe = () => {
    clearResubscribeTimer();
    resubscribeAttempt += 1;
    const delay = Math.min(RESUBSCRIBE_MAX_DELAY_MS, RESUBSCRIBE_BASE_DELAY_MS * 2 ** (resubscribeAttempt - 1));
    resubscribeTimer = setTimeout(() => {
      resubscribeTimer = null;
      // Re-check permission rather than calling `subscribe` blind: the user may
      // have revoked it in the meantime, and retrying then would be a nag.
      if (Notification.permission === 'granted') void subscribe();
    }, delay);
  };

  /** Web-push subscription flow: before the browser permission prompt, the
   * app shows a short plain-language explainer of why notifications matter
   * (`PushPermissionExplainer.vue`); this only performs the actual
   * subscribe, called once the explainer has been accepted (or permission
   * is already granted). */
  const subscribe = async (): Promise<void> => {
    try {
      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        const { data } = await axiosApi.get('punch-anomalies/vapid-public-key');
        if (!data.key) {
          state.value = 'missing';
          return;
        }
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.key) as unknown as BufferSource,
        });
      }

      const json = subscription.toJSON();
      if (!json.endpoint || !json.keys) {
        state.value = 'missing';
        return;
      }
      await axiosApi.post('punch-anomalies/push-subscriptions', {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      });
      state.value = 'subscribed';
      // A later regression back to `missing` deserves a fresh warning even in
      // the same boot — this boot's dismissal covered the episode that ended.
      warningDismissed.value = false;
      resubscribeAttempt = 0;
      clearResubscribeTimer();
    } catch (error) {
      console.warn('Push subscription failed:', error);
      state.value = 'missing';
      // The browser side may well have succeeded — what usually fails here is
      // the request registering the subscription with the server. Nothing else
      // in the app ever retried this, so one dropped request at boot meant no
      // push for the rest of the shift, with only a banner the coach had no
      // way to act on beyond noticing it.
      scheduleResubscribe();
    }
  };

  /**
   * Called on login/app start. Never shows the browser's own permission
   * prompt by itself — that only happens after the explainer is accepted
   * (`requestPermissionAndSubscribe`) or permission is already granted.
   */
  const ensureSubscribed = async (): Promise<void> => {
    if (!evaluateSupport()) {
      state.value = 'unsupported';
      return;
    }

    permissionBlocked.value = Notification.permission === 'denied';
    if (permissionBlocked.value) {
      state.value = 'missing';
      return;
    }
    if (Notification.permission === 'default') {
      // "Plus tard" is session-scoped (`declineExplainer`), and this runs
      // again on every MainLayout remount — overwriting the deferral here
      // popped the declined explainer right back open mid-session.
      if (state.value !== 'deferred') state.value = 'needs-explainer';
      return;
    }
    await subscribe();
  };

  /**
   * The warning banner's retry: an explicit request to be asked again, so it
   * clears the session deferral `ensureSubscribed` otherwise respects.
   */
  const retrySubscription = async (): Promise<void> => {
    if (state.value === 'deferred') state.value = 'unknown';
    await ensureSubscribed();
  };

  /** Explainer accepted: now (and only now) triggers the actual browser permission prompt. */
  const requestPermissionAndSubscribe = async (): Promise<void> => {
    try {
      const permission = await Notification.requestPermission();
      permissionBlocked.value = permission === 'denied';
      if (permission !== 'granted') {
        state.value = 'missing';
        return;
      }
      await subscribe();
    } catch (error) {
      console.warn('Push permission request failed:', error);
      state.value = 'missing';
    }
  };

  /**
   * Explainer declined for now: stop asking this session, keep the warning
   * honest. Its own state, not `missing` — the button says "Plus tard", and
   * setting the same state a hard denial sets made it mean "no", permanently
   * and with no way back. Deferred still warns, because the safety net really
   * is off, but it stays retryable.
   */
  const declineExplainer = (): void => {
    state.value = 'deferred';
  };

  /** Arms the explainer for the next `needs-explainer` state. */
  const allowExplainer = (): void => {
    explainerAllowed.value = true;
  };

  const showExplainer = computed(() => state.value === 'needs-explainer' && explainerAllowed.value);

  /** This device has no working push channel, whatever the reason. */
  const isMissing = computed(() => state.value === 'missing' || state.value === 'unsupported');

  /** Drives the floating banner only — the settings row ignores the dismissal.
   * Deferred still warns: the safety net really is off, just retryable. */
  const showWarning = computed(
    () => (isMissing.value || state.value === 'deferred') && !warningDismissed.value,
  );

  const dismissWarning = (): void => {
    warningDismissed.value = true;
  };

  /**
   * On logout of a shared device the browser must stop receiving the next
   * user's (or ex-employee's) anomaly payloads.
   */
  const unsubscribe = async (): Promise<void> => {
    // A backoff retry armed by an earlier failed subscribe must never fire
    // after logout — its only guard is the browser permission, which stays
    // 'granted' on a shared device, so it would re-enrol this device for
    // whoever holds the session next.
    clearResubscribeTimer();
    resubscribeAttempt = 0;

    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    try {
      // getRegistration(), not `ready`: logout awaits this promise before it
      // posts /logout, and `ready` never settles when no service worker ever
      // activates (registration blocked or failed, e.g. private browsing) —
      // which would leave the user silently signed in on a shared device.
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) return;

      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) return;

      const endpoint = subscription.endpoint;
      await subscription.unsubscribe();
      await axiosApi.delete('punch-anomalies/push-subscriptions', { data: { endpoint } });
    } catch (error) {
      console.warn('Push unsubscribe failed:', error);
    }
  };

  return {
    state,
    isMissing,
    showWarning,
    dismissWarning,
    showExplainer,
    allowExplainer,
    permissionBlocked,
    ensureSubscribed,
    retrySubscription,
    requestPermissionAndSubscribe,
    declineExplainer,
    unsubscribe,
  };
};
